import { type CSSProperties, type FC, useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { cx } from "@emotion/css";
import { Button } from "@snapchat/snap-design-system";
import { ChevronDownIcon } from "@snapchat/snap-design-system-icons";

import { AbsoluteRoute } from "../../../../core/routes";
import { type RemoteApiSpec } from "../../../../gql-generated/types";
import { TruncateComponent } from "../../../../shared/components";
import { LensUpdateAtDate } from "../../common";
import { DropdownMenu } from "../../dropdown-menu";
import { baseWidth, dropdownWidth, idWidth, nameWidth } from "../header";

import {
    ActionSection,
    ApiClickableContainer,
    ApiContainer,
    ApiDataContainer,
    StatusSection,
    tableTextStyle,
} from "./ApiRow.styles";
import { ApiStatusBadge } from "./ApiStatusBadge";

type OnDelete = (api: RemoteApiSpec) => Promise<void>;

interface ApiRow {
    style: CSSProperties;
    api: RemoteApiSpec;
    onDelete: OnDelete;
    deleteLoading?: boolean;
}

export const ApiRow: FC<ApiRow> = ({ api, style, onDelete, deleteLoading = false }) => {
    const { id, name, updatedAt, approvalState } = api;

    const navigate = useNavigate();
    const goToApi = useCallback(() => {
        if (!id) {
            throw new Error("Missing API ID");
        }
        const route = generatePath(AbsoluteRoute.API, { apiId: id });
        navigate(route);
    }, [navigate, id]);

    return (
        <ApiContainer style={style}>
            <ApiDataContainer>
                <ApiClickableContainer onClick={goToApi}>
                    <TruncateComponent content={name} className={cx(tableTextStyle, nameWidth)} />
                    <TruncateComponent content={id} className={cx(tableTextStyle, idWidth)} />
                    <LensUpdateAtDate seconds={new Date(updatedAt).getTime() / 1000} />

                    <StatusSection className={baseWidth}>{<ApiStatusBadge status={approvalState} />}</StatusSection>
                </ApiClickableContainer>
                <ActionSection className={dropdownWidth}>
                    <DropdownMenu deleteLoading={deleteLoading} api={api} onDelete={onDelete}>
                        <Button shape="circle">
                            <ChevronDownIcon />
                        </Button>
                    </DropdownMenu>
                </ActionSection>
            </ApiDataContainer>
        </ApiContainer>
    );
};
